import { EXPERT_KNOBS } from '../bot/levels.ts';
import type { BotProfileKnobs } from './types.ts';

/**
 * How much slower than Expert a profile sails, from solo playtests (2026-10-08: 6 maps,
 * 3 seeds, Skiff, `measurePace`). Each table is boat-clock as a share of Expert's with that one
 * knob changed from Expert. What the runs showed:
 * - Aim error is what costs time, and it is what crashes and makes one race unlike the next.
 * - Slips (`temperature`) cost a few percent, steadily, with no crashes.
 * - Distance-only course sense costs about a tenth, steadily; at foresight 3 it turns wild.
 * - Foresight alone (time sense) and brake confidence alone cost nothing in a solo race.
 * - A speed limit costs as much as it is set to, with no crashes and no spread between races.
 *
 * Combined, the steady part counts in full alone and about half once aim error is large:
 * pace = 1 + w(aim)·(steady − 1) + (aimPace − 1), w from 1 down to 0.5 by 3 m of aim. That
 * reproduces the calibrated Easy, Medium, and Hard and the mixed runs within about 3%.
 */
type Curve = readonly (readonly [number, number])[];

/** Aim error (m) → pace. Measured 1.5 / 3 / 4.5 / 6 / 9 m: 111 / 128 / 143 / 134 / 149%, smoothed to rise. */
const AIM_PACE: Curve = [
  [0, 1],
  [1.5, 1.11],
  [3, 1.25],
  [4.5, 1.35],
  [6, 1.4],
  [9, 1.49],
];

/** Slips (turns) → pace. Measured 0.3 / 0.75 / 1.5 / 2.5: 102 / 103 / 103 / 104%. */
const SLIP_PACE: Curve = [
  [0, 1],
  [0.3, 1.02],
  [0.75, 1.031],
  [1.5, 1.033],
  [2.5, 1.038],
];

/** Foresight (turns) with distance-only sense → pace. Measured 8 / 6 / 4 / 3: 110 / 114 / 115 / 177%. */
const DISTANCE_PACE: Curve = [
  [1, 2],
  [3, 1.77],
  [4, 1.145],
  [6, 1.135],
  [8, 1.1],
];

/**
 * Speed limit (display km/h) → pace, measured 2026-10-08 the same way. Expert's mean speed on
 * these maps is 48–68 km/h, its top 83–129. No crashes at any limit, and the seed changes
 * nothing. Below 28 km/h it is not measured and reads as 28.
 */
const LIMIT_PACE: Curve = [
  [28, 2.292],
  [35, 1.777],
  [45, 1.429],
  [52, 1.272],
  [60, 1.156],
  [80, 1.031],
  [100, 1],
];

/** Aim error at which the steady part counts half. */
const AIM_FOR_HALF_WEIGHT_M = 3;

/** The most aim error the bars use: the end of the measured range. */
export const MAX_DIAL_AIM_M = 9;
/** The slowest steady part the bars use: distance-only sense at full foresight, plus the most slips. */
export const STEADY_SLIPS_MAX = 2.5;

function along(curve: Curve, x: number): number {
  const first = curve[0]!;
  const last = curve.at(-1)!;

  if (x <= first[0]) {
    return first[1];
  }

  for (let index = 1; index < curve.length; index += 1) {
    const [x1, y1] = curve[index]!;
    const [x0, y0] = curve[index - 1]!;

    if (x <= x1) {
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }

  return last[1];
}

/** The x a rising curve reaches `y` at, clamped to its ends. */
function inverse(curve: Curve, y: number): number {
  const first = curve[0]!;
  const last = curve.at(-1)!;

  if (y <= first[1]) {
    return first[0];
  }

  for (let index = 1; index < curve.length; index += 1) {
    const [x1, y1] = curve[index]!;
    const [x0, y0] = curve[index - 1]!;

    if (y <= y1) {
      return x0 + ((x1 - x0) * (y - y0)) / (y1 - y0);
    }
  }

  return last[0];
}

export function aimPace(aimErrorM: number): number {
  return along(AIM_PACE, aimErrorM);
}

export function aimForPace(pace: number): number {
  return inverse(AIM_PACE, pace);
}

export function slipPace(temperature: number): number {
  return along(SLIP_PACE, temperature);
}

export function slipsForPace(pace: number): number {
  return inverse(SLIP_PACE, pace);
}

/** Pace under a speed limit (km/h); 0 has none. */
export function limitPace(speedLimitKmh: number): number {
  return speedLimitKmh > 0 ? along(LIMIT_PACE, speedLimitKmh) : 1;
}

/** The speed limit (km/h) that sails at `pace`, within the measured range; 0 when none is needed. */
export function limitForPace(pace: number): number {
  if (pace <= 1) {
    return 0;
  }

  // The curve falls as the limit rises: walk it from the fast end.
  for (let index = LIMIT_PACE.length - 1; index > 0; index -= 1) {
    const [x1, y1] = LIMIT_PACE[index]!;
    const [x0, y0] = LIMIT_PACE[index - 1]!;

    if (pace <= y0) {
      return x1 + ((x0 - x1) * (pace - y1)) / (y0 - y1);
    }
  }

  return LIMIT_PACE[0]![0];
}

/** The slowest speed limit the bars use: the end of the measured range. */
export const MIN_DIAL_SPEED_LIMIT_KMH = LIMIT_PACE[0]![0];

/** Distance-only sense at full foresight. */
export const DISTANCE_SENSE_PACE = along(DISTANCE_PACE, EXPERT_KNOBS.depth);

/**
 * The steady part: course sense (with foresight), slips, and a speed limit. Foresight alone costs
 * nothing. The bars never combine a limit with the other two, so how they stack is not measured.
 */
export function steadyPace(
  knobs: Pick<BotProfileKnobs, 'courseSense' | 'depth' | 'temperature' | 'speedLimitKmh'>,
): number {
  const sense = knobs.courseSense === 'distance' ? along(DISTANCE_PACE, knobs.depth) : 1;
  return sense * slipPace(knobs.temperature) * limitPace(knobs.speedLimitKmh);
}

/** How much of the steady part counts at this aim error: all of it at none, half from 3 m. */
export function steadyWeight(aimErrorM: number): number {
  return 1 - 0.5 * Math.min(1, aimErrorM / AIM_FOR_HALF_WEIGHT_M);
}

/** Pace from the two parts, as the combined runs measured. */
export function combinedPace(steady: number, aimErrorM: number): number {
  return 1 + steadyWeight(aimErrorM) * (steady - 1) + (aimPace(aimErrorM) - 1);
}

/**
 * A thinner search than Expert's: not measured, so a light guess (up to 10% at a quarter of
 * Expert's width, density, first-click samples, and checks).
 */
function searchPace(knobs: BotProfileKnobs): number {
  const shortfall =
    [
      knobs.width / EXPERT_KNOBS.width,
      knobs.density / EXPERT_KNOBS.density,
      knobs.rootDensity / EXPERT_KNOBS.rootDensity,
      knobs.verify / EXPERT_KNOBS.verify,
    ]
      .map((share) => Math.min(1, Math.max(0, 1 - share)))
      .reduce((sum, part) => sum + part, 0) / 4;
  return 1 + 0.13 * shortfall;
}

/** Estimated boat-clock as a share of Expert's (1 is Expert's pace, 1.4 is 40% slower). */
export function estimatedPace(knobs: BotProfileKnobs): number {
  return combinedPace(steadyPace(knobs), knobs.aimErrorM) * searchPace(knobs);
}
